import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Move,
  Play,
  Users,
  User,
  Building,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  Save,
  X,
} from 'lucide-react';
import { adminService } from '../services/adminService';

const TutorialVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    user_type: 'both',
    is_active: true,
    display_order: 0,
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    brand: 0,
    influencer: 0,
    both: 0,
    active: 0,
    inactive: 0,
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await adminService.getTutorialVideos();
      console.log('API Response:', response); // Debug log
      setVideos(response.data || []);
      calculateStats(response.data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (videoList) => {
    const total = videoList.length;
    const brand = videoList.filter(v => v.user_type === 'brand').length;
    const influencer = videoList.filter(v => v.user_type === 'influencer').length;
    const both = videoList.filter(v => v.user_type === 'both').length;
    const active = videoList.filter(v => v.is_active).length;
    const inactive = total - active;

    setStats({ total, brand, influencer, both, active, inactive });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingVideo) {
        await adminService.updateTutorialVideo(editingVideo.id, formData);
        toast.success('Video updated successfully');
      } else {
        await adminService.createTutorialVideo(formData);
        toast.success('Video created successfully');
      }
      
      setShowModal(false);
      setEditingVideo(null);
      resetForm();
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error(error.response?.data?.message || 'Failed to save video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      youtube_url: video.youtube_url,
      user_type: video.user_type,
      is_active: video.is_active,
      display_order: video.display_order,
    });
    setShowModal(true);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await adminService.deleteTutorialVideo(videoId);
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const handleToggleActive = async (video) => {
    try {
      await adminService.updateTutorialVideo(video.id, {
        ...video,
        is_active: !video.is_active,
      });
      toast.success(`Video ${!video.is_active ? 'activated' : 'deactivated'}`);
      fetchVideos();
    } catch (error) {
      console.error('Error toggling video status:', error);
      toast.error('Failed to update video status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      youtube_url: '',
      user_type: 'both',
      is_active: true,
      display_order: 0,
    });
  };

  const handleReorder = async (newOrder) => {
    try {
      await adminService.reorderTutorialVideos(newOrder);
      toast.success('Videos reordered successfully');
      fetchVideos();
    } catch (error) {
      console.error('Error reordering videos:', error);
      toast.error('Failed to reorder videos');
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDropTargetIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }

    const reorderedVideos = [...videos];
    const draggedVideo = reorderedVideos[draggedIndex];
    
    // Remove the dragged item
    reorderedVideos.splice(draggedIndex, 1);
    
    // Insert at new position
    reorderedVideos.splice(dropIndex, 0, draggedVideo);
    
    // Update display orders
    const newOrder = reorderedVideos.map((video, index) => ({
      id: video.id,
      display_order: index,
    }));
    
    setVideos(reorderedVideos);
    handleReorder(newOrder);
    
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const moveVideo = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= videos.length) return;
    
    const reorderedVideos = [...videos];
    const temp = reorderedVideos[index];
    reorderedVideos[index] = reorderedVideos[newIndex];
    reorderedVideos[newIndex] = temp;
    
    const newOrder = reorderedVideos.map((video, idx) => ({
      id: video.id,
      display_order: idx,
    }));
    
    setVideos(reorderedVideos);
    handleReorder(newOrder);
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'brand':
        return <Building className="h-4 w-4 text-blue-500" />;
      case 'influencer':
        return <User className="h-4 w-4 text-purple-500" />;
      case 'both':
        return <Users className="h-4 w-4 text-green-500" />;
      default:
        return <Users className="h-4 w-4 text-gray-500" />;
    }
  };

  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'brand':
        return 'Brands Only';
      case 'influencer':
        return 'Influencers Only';
      case 'both':
        return 'Both';
      default:
        return 'Both';
    }
  };

  const extractVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tutorial Videos</h1>
          <p className="text-gray-600">Manage help and tutorial videos for users</p>
        </div>
        <button
          onClick={() => {
            setEditingVideo(null);
            resetForm();
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Video
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Videos</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-red-600">{stats.inactive}</div>
          <div className="text-sm text-gray-600">Inactive</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-purple-600">{stats.influencer}</div>
          <div className="text-sm text-gray-600">Influencer</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-blue-600">{stats.brand}</div>
          <div className="text-sm text-gray-600">Brand</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="text-2xl font-bold text-green-600">{stats.both}</div>
          <div className="text-sm text-gray-600">Both</div>
        </div>
      </div>

      {/* Videos List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Videos</h2>
          
          {videos.length === 0 ? (
            <div className="text-center py-8">
              <Play className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No videos</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new tutorial video.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {videos.map((video, index) => (
                <div
                  key={video.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`border rounded-lg p-4 transition-all ${
                    draggedIndex === index ? 'opacity-50' : ''
                  } ${
                    dropTargetIndex === index ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } hover:border-gray-300`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt={video.title}
                          className="h-16 w-24 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="h-16 w-24 bg-gray-200 rounded-lg flex items-center justify-center">
                          <Play className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-sm text-gray-600 mt-1 truncate">
                              {video.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              {getUserTypeIcon(video.user_type)}
                              <span>{getUserTypeLabel(video.user_type)}</span>
                            </div>
                            <span>{video.view_count} views</span>
                            <span>Order: {video.display_order}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          {/* Reorder buttons */}
                          <div className="flex flex-col">
                            <button
                              onClick={() => moveVideo(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => moveVideo(index, 'down')}
                              disabled={index === videos.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </button>
                          </div>

                          {/* Status toggle */}
                          <button
                            onClick={() => handleToggleActive(video)}
                            className={`p-2 rounded-lg ${
                              video.is_active
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                          >
                            {video.is_active ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </button>

                          {/* Edit */}
                          <button
                            onClick={() => handleEdit(video)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => handleDelete(video.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>

                          {/* Drag handle */}
                          <div className="cursor-move p-2 text-gray-400">
                            <Move className="h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube URL *
                </label>
                <input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                {formData.youtube_url && extractVideoId(formData.youtube_url) && (
                  <div className="mt-2">
                    <img
                      src={`https://img.youtube.com/vi/${extractVideoId(formData.youtube_url)}/hqdefault.jpg`}
                      alt="Video thumbnail"
                      className="h-20 w-auto rounded"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="both">Both Brands & Influencers</option>
                  <option value="brand">Brands Only</option>
                  <option value="influencer">Influencers Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active (visible to users)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {editingVideo ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialVideos;
